import useApi from 'hooks/useApi';
import { requiredCheckbox, stringRequired } from 'libs/validations';
import React, { FunctionComponent, useEffect } from 'react';
import * as Yup from 'yup';
import { YourContact } from 'pages/Payments/PaymentMethods/Cards';
import PaymentMethodLayout from 'pages/Payments/PaymentMethods/Layout';
import { IInvoice } from './ChoosePaymentType';
import useFetchResource from 'hooks/useFetchResource';
import api from 'libs/api';
import useOnSubmit from 'hooks/useOnSubmit';
import { FormikProps } from 'formik';
import { CreditCard } from 'pages/Payments/PaymentMethods/Cards/CreditCard';
import OverlaySpinner from 'components/OverlaySpinner';
import { useParams } from 'react-router-dom';
import useStripeCardSetup, { IStripeCardFormValues } from 'hooks/useStripeCardSetup';

export interface ICreditCardFormValues extends IStripeCardFormValues {
  is_authorized_to_payments: boolean;
  user_email: string;
  user_name: string;
  user_position: string;
}

const values: ICreditCardFormValues = {
  is_authorized_to_payments: false,
  user_email: '',
  user_name: '',
  user_position: '',
  credit_card_number: '',
  credit_card_expiry: '',
  credit_card_cvc: '',
};

const validationSchema = Yup.object().shape({
  is_authorized_to_payments: requiredCheckbox,
  user_email: stringRequired,
  user_name: stringRequired,
  user_position: stringRequired,
});

const formCards = [
  {
    component: (props: FormikProps<ICreditCardFormValues>) => <CreditCard {...props} />,
    title: 'Credit Card Information',
  },
  {
    component: () => <YourContact />,
    title: 'Your contact information',
  },
];

const initialValues = {
  id: 0,
  guid: '',
  plan_name: '',
  total_price: 0,
  is_paid: false,
  is_processing: false,
  payment_types: {
    credit_card: false,
    debit_card: false,
  },
};

const CreditCardPage: FunctionComponent = () => {
  const { guid } = useParams();
  const { getApiPath } = useApi();
  const { resource: invoice, isLoading } = useFetchResource<IInvoice>({
    api: api.account.billing.invoices.getOne,
    initialParams: {
      guid,
    },
    initialValues,
  });

  const onSubmit = useOnSubmit({
    api: getApiPath('billing.invoices.payment.credit', 'create'),
    deps: [guid],
    onSuccess: () => (window.location.href = '/dashboard'),
    params: { guid },
    serializer: (data: ICreditCardFormValues) => ({ resource: data }),
    successMessage: 'Payment successfully saved',
  });

  const submitHandler = useStripeCardSetup<ICreditCardFormValues>({
    setupIntentApi: getApiPath('billing.invoices.payment.credit.setupIntent', 'create'),
    params: { guid },
    onSubmit,
    deps: [guid],
  });

  useEffect(() => {
    if (invoice?.is_paid || invoice?.is_processing) {
      window.location.href = `/invoices/${invoice.guid}/choose-payment-type`;
    }
  }, [invoice]);

  return (
    <>
      {isLoading ? (
        <OverlaySpinner visible={isLoading} />
      ) : (
        <PaymentMethodLayout<ICreditCardFormValues>
          plan={invoice.plan_name}
          price={invoice.total_price.toString()}
          invoiceID={invoice.id}
          initialValues={values}
          validationSchema={validationSchema}
          title="Credit Card"
          formCards={formCards}
          onSubmit={submitHandler}
        />
      )}
    </>
  );
};

export default CreditCardPage;
